//#region imports
import React, { useCallback, useMemo } from "react";

// custom components
import JobList from "../job-components/job-list";
import EmployeeInformation from "../employee-components/employee-information";
import TimelineColumnContainer from "./timeline-column-container";

// styling
import { Colors } from "_styles";

// timeline constants
import { EmployeeInformationColumnWidth, TimelineColumnHeight, TimelineColumnWidthRaw } from "../timeline-constants";

// redux
import { useSelector, useDispatch } from "react-redux";
import { setShowConfirmationModal, selectSelectedTasksList, setShowBreakTaskModal, setBreakTaskEmployee, setShowAdhocTaskModal, setAdhocTaskEmployee } from "_redux/slices/timeline/selected-task";
import { selectThirtyMinLayout, selectTimelineDate, selectTimelineIndex } from "_redux/slices/timeline/timeline-control";
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// functions 
import { addFiveMinuteOffset } from "_utils/functions";
import RedLine from "../job-components/timeline-jobs/red-line";
import { setClickedEmployeeId } from "_redux/slices/timeline/employees";

//#endregion

const TimelineRow = React.memo((props) => {

  //#region redux

  const dispatch = useDispatch();
  const selectedTasksList = useSelector(selectSelectedTasksList);
  const timelineDate = useSelector(selectTimelineDate);                                 // select timeline Date
  const thirtyMinLayout = useSelector(selectThirtyMinLayout);
  const timeIndex = useSelector(selectTimelineIndex);
  const usedTheme = useSelector(selectUsedTheme);

  //#endregion

  // timelineHour comes from parent (EmployeeList) via props — single timer for all rows
  const timelineHour = props?.timelineHour;

  // Exact shift boundaries as ms — used for per-slot coloring
  const shiftStartMs = useMemo(() => {
    const s = props?.employee?.shift?.start;
    if (!s) return null;
    return s?.seconds != null ? s.seconds * 1000 : new Date(s).getTime();
  }, [props?.employee?.shift?.start]);

  const shiftEndMs = useMemo(() => {
    const e = props?.employee?.shift?.end;
    if (!e) return null;
    const endMs = e?.seconds != null ? e.seconds * 1000 : new Date(e).getTime();
    return shiftStartMs != null && endMs <= shiftStartMs ? endMs + 24 * 60 * 60 * 1000 : endMs;
  }, [props?.employee?.shift?.end, shiftStartMs]);

  //#region functions

  // get the time line row clicked column - memoized to prevent re-renders
  const handleColumnClick = useCallback((column) => {
    if (props.employee.userId) {
      dispatch(setClickedEmployeeId(props.employee.userId))
    }
    // check if there is a selcted task list to assign    
    if (Array.isFullArray(selectedTasksList)) {
      /* create id to save which column was clicked */
      const columnId = column + "-" + props?.employee?.employeeId;
      props?.setClickedColumn(columnId);
      // show confirmation modal
      dispatch(setShowConfirmationModal(true));
    }
    else {
      // show adhoc task modal
      dispatch(setShowAdhocTaskModal(true))
      dispatch(setAdhocTaskEmployee(props?.employee))
    }
  }, [selectedTasksList, props?.employee?.employeeId, props?.setClickedColumn, dispatch]);

  // open break/task modal - memoized
  const openBreakTaskModal = useCallback(() => {
    dispatch(setShowBreakTaskModal(true))
    dispatch(setBreakTaskEmployee(props?.employee))
  }, [dispatch, props?.employee]);

  // open adhoc task modal - memoized
  const openAdhocTaskModal = useCallback(() => {
    dispatch(setShowAdhocTaskModal(true))
    dispatch(setAdhocTaskEmployee(props?.employee))
  }, [dispatch, props?.employee]);

  const getColumnShiftBackground = useCallback((columnId) => {
    if (shiftStartMs == null || shiftEndMs == null) return usedTheme.BACKGROUND_COLOR;
    const [colHour, colMin] = columnId.split(':').map(Number);
    const colStart = new Date(timelineDate);
    colStart.setHours(colHour, colMin, 0, 0);

    const slotDurationMs = (thirtyMinLayout ? 30 : 60) * 60 * 1000;
    const colStartMs = colStart.getTime();
    const colEndMs = colStartMs + slotDurationMs;
    const overlapStartMs = Math.max(colStartMs, shiftStartMs);
    const overlapEndMs = Math.min(colEndMs, shiftEndMs);

    if (overlapStartMs >= overlapEndMs) return usedTheme.BACKGROUND_COLOR;

    const startPercent = ((overlapStartMs - colStartMs) / slotDurationMs) * 100;
    const endPercent = ((overlapEndMs - colStartMs) / slotDurationMs) * 100;

    if (startPercent <= 0 && endPercent >= 100) return usedTheme.TIMELINE_COLOR;

    return `linear-gradient(to right, ${usedTheme.BACKGROUND_COLOR} 0%, ${usedTheme.BACKGROUND_COLOR} ${startPercent}%, ${usedTheme.TIMELINE_COLOR} ${startPercent}%, ${usedTheme.TIMELINE_COLOR} ${endPercent}%, ${usedTheme.BACKGROUND_COLOR} ${endPercent}%, ${usedTheme.BACKGROUND_COLOR} 100%)`;
  }, [shiftStartMs, shiftEndMs, timelineDate, thirtyMinLayout, usedTheme.BACKGROUND_COLOR, usedTheme.TIMELINE_COLOR]);

  // check if two dates are the same day
  function isSameDay(d1, d2) {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }
  //#endregion


  // Memoize isSameDay check
  const isToday = useMemo(() => isSameDay(new Date(), timelineDate), [timelineDate]);

  // Horizontal virtualization: only mount columns within visible range
  const colW = TimelineColumnWidthRaw;
  const totalCols = Array.isArray(timeIndex) ? timeIndex.length : 0;
  const vStart = Math.max(0, props.visibleStart ?? 0);
  const vEnd = Math.min(totalCols - 1, props.visibleEnd ?? totalCols - 1);
  const leftSpacerWidth = vStart * colW;
  const rightSpacerWidth = Math.max(0, (totalCols - 1 - vEnd)) * colW;

  return (
    <>
      <div style={{ display: "flex", height: TimelineColumnHeight, minHeight: TimelineColumnHeight, maxHeight: TimelineColumnHeight }}>

        {/* employee profile */}
        <div style={{
          position: "sticky",
          left: 0, zIndex: 253,
          height: TimelineColumnHeight,
        }}>
          <div style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            height: TimelineColumnHeight,
            width: EmployeeInformationColumnWidth,
            borderRight: `1px solid ${usedTheme.BORDER_COLOR}`,
            backgroundColor: usedTheme.BACKGROUND_COLOR,
          }}>
            <EmployeeInformation id={props?.employee?.employeeId} employeeShift={props?.employee?.shift} openBreakTaskModal={openBreakTaskModal} openAdhocTaskModal={openAdhocTaskModal} employee={props?.employee} index={props?.index} employeeArrayLength={props?.arrayLength} />
          </div>
        </div>

        {/* left spacer for off-screen columns */}
        {leftSpacerWidth > 0 && (
          <div style={{ width: leftSpacerWidth, minWidth: leftSpacerWidth, flexShrink: 0 }} />
        )}

        {/* only mount columns within the visible scroll range */}
        {Array.isArray(timeIndex) && timeIndex.slice(vStart, vEnd + 1).map((columnId, sliceIdx) => {
          const index = vStart + sliceIdx;
          const shiftBackground = getColumnShiftBackground(columnId);
          return (
            <div key={index}>

              {/* column container */}
              <TimelineColumnContainer
                backgroundColor={usedTheme.BACKGROUND_COLOR}
                background={shiftBackground}
                borderColor={props?.clickedColumn == columnId + "-" + props?.employee?.employeeId ? Colors.PRM_LIGHT_BLUE : usedTheme.BORDER_COLOR}
                onClick={() => { handleColumnClick(columnId) }}
                is30MinLayout={thirtyMinLayout}
                index={index}
              >


                {/* Red line div */}
                {columnId == timelineHour && isToday && <RedLine index={props?.index} />}

                {/* render 5-min slices when 30-min layout is active */}
                {thirtyMinLayout ? (
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', height: '100%', width: '100%' }}>
                    {[0, 1, 2, 3, 4, 5].map((interval) => (
                      <div
                        key={`${columnId}-${interval}`}
                        style={{
                          flex: 1,
                          width: '100%',
                          height: '100%',
                          minWidth: 0,
                          position: 'relative',
                        }}
                      >

                        <JobList
                          employeeId={props?.employee?.employeeId}
                          hour={columnId}
                          timeIndex={addFiveMinuteOffset(columnId, interval)}
                          id={columnId + "-" + props?.employee?.employeeId}
                          disabled={true}
                        />

                      </div>
                    ))}
                  </div>
                )
                  :
                  (
                    /* render full hour job list when not in 30-min layout */
                    <JobList
                      employeeId={props?.employee?.employeeId}
                      hour={columnId}
                      id={columnId + "-" + props?.employee?.employeeId}
                      disabled={true}
                      timeIndex={columnId}
                    />
                  )}

              </TimelineColumnContainer>
            </div>


          );
        })}

        {/* right spacer for off-screen columns */}
        {rightSpacerWidth > 0 && (
          <div style={{ width: rightSpacerWidth, minWidth: rightSpacerWidth, flexShrink: 0 }} />
        )}

      </div>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if these specific props change
  return prevProps.employee?.employeeId === nextProps.employee?.employeeId &&
    prevProps.clickedColumn === nextProps.clickedColumn &&
    prevProps.index === nextProps.index &&
    prevProps.arrayLength === nextProps.arrayLength &&
    prevProps.employee?.shift?.start === nextProps.employee?.shift?.start &&
    prevProps.employee?.shift?.end === nextProps.employee?.shift?.end &&
    prevProps.timelineHour === nextProps.timelineHour &&
    prevProps.visibleStart === nextProps.visibleStart &&
    prevProps.visibleEnd === nextProps.visibleEnd;
});

export default TimelineRow;